/* eslint-disable @typescript-eslint/no-explicit-any */
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Typography } from '@mui/material';
import React from 'react';
import ReactDOM from 'react-dom';
import { useDragState } from '../useDragState';
import { WidgetEditorContext } from '../WidgetEditorContext';
import { ToolbarButton } from './Components/ToolbarButton';
export const WidgetEditorOverlay = (props) => {
    var _a;
    const widgetEditorContext = React.useContext(WidgetEditorContext);
    const [dragElement, dropElement, handlePointerDown, handlePointerUp, handlePointerMove] = useDragState(widgetEditorContext, props.moveWidget);
    const overlayRef = React.useRef(undefined);
    const id = widgetEditorContext.getInternalId(props.config);
    const selected = widgetEditorContext.getSelectedWidgetId() === id;
    const zIndex = (selected ? selectedZIndex : baseZIndex) + ((_a = props.nestingLevel) !== null && _a !== void 0 ? _a : 0);
    const handleEdit = (event) => {
        const editorPath = widgetEditorContext.calculateEditorPath(event.target);
        if (editorPath && props.onEditWidget) {
            widgetEditorContext.lockWidgetForEdit();
            props.onEditWidget((updatedWidget) => {
                widgetEditorContext.unlockWidgetForEdit();
                if (updatedWidget != null) {
                    widgetEditorContext.updateWidget(editorPath, updatedWidget);
                }
            });
        }
    };
    const handleDelete = (event) => {
        const editorPath = widgetEditorContext.calculateEditorPath(event.target);
        if (editorPath) {
            widgetEditorContext.deleteWidget(editorPath);
            if (window.document.activeElement) {
                window.document.activeElement.blur();
            }
        }
    };
    if (widgetEditorContext.editMode) {
        return (React.createElement(React.Fragment, null,
            React.createElement(Box, { tabIndex: 0, sx: {
                    position: 'absolute',
                    zIndex: zIndex,
                    top: selected ? -selectedOutlineOffset : 0,
                    right: selected ? -selectedOutlineOffset : 0,
                    bottom: selected ? -selectedOutlineOffset : 0,
                    left: selected ? -selectedOutlineOffset : 0,
                    outline: selected ? selectedOverlayOutline : 'none',
                    outlineOffset: selected ? 0 : selectedOutlineOffset,
                    '&:hover': {
                        outline: selected ? selectedOverlayOutline : hoverOverlayOutline,
                    },
                    backgroundColor: selected ? selectedOverlayBackgroundColor : 'none',
                    touchAction: 'none',
                }, onPointerDown: (event) => {
                    event.stopPropagation();
                    event.target.focus();
                    handlePointerDown(event);
                }, onPointerUp: handlePointerUp, onPointerMove: handlePointerMove, onFocus: (event) => {
                    widgetEditorContext.selectWidget(id, event.target);
                }, onBlur: (event) => {
                    if (event.relatedTarget) {
                        let relatedTarget = event.relatedTarget;
                        while (relatedTarget) {
                            if (relatedTarget === overlayRef.current) {
                                return;
                            }
                            relatedTarget = relatedTarget.parentElement;
                        }
                    }
                    if (!widgetEditorContext.isSelectedWidgetLockedForEdit()) {
                        widgetEditorContext.selectWidget();
                    }
                }, onKeyDown: (event) => {
                    if (selected) {
                        switch (event.key) {
                            case 'Backspace':
                            case 'Delete':
                                handleDelete(event);
                                break;
                            case 'e':
                                handleEdit(event);
                                break;
                        }
                    }
                }, "data-editor-widget-id": id }, selected && (React.createElement(React.Fragment, null,
                React.createElement(Box, { sx: {
                        position: 'absolute',
                        zIndex: zIndex + 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        top: -selectedToolbarSize,
                        left: -selectedOverlayOutlineWidth,
                        width: `calc(100% + ${selectedOverlayOutlineWidth * 2}px)`,
                        height: selectedToolbarSize,
                        pointerEvents: 'none',
                    } },
                    React.createElement(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                            pl: 1,
                            pr: 1,
                            backgroundColor: selectedLabelBackgroundColor,
                            color: selectedLabelColor,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'auto',
                            cursor: 'move',
                            userSelect: 'none',
                        } },
                        React.createElement(Typography, { component: 'div', sx: { fontSize: 14 } }, props.label)),
                    React.createElement(Box, { sx: {
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                            backgroundColor: selectedLabelBackgroundColor,
                            color: selectedLabelColor,
                            pointerEvents: 'auto',
                        }, onPointerDown: (event) => {
                            event.stopPropagation();
                        } },
                        props.onEditWidget && (React.createElement(ToolbarButton, { size: selectedToolbarSize, onClick: handleEdit },
                            React.createElement(SettingsIcon, { fontSize: 'inherit' }))),
                        React.createElement(ToolbarButton, { size: selectedToolbarSize, onClick: handleDelete },
                            React.createElement(DeleteIcon, { fontSize: 'inherit' })))),
                props.children && (React.createElement(Box, { ref: overlayRef, onPointerDown: (event) => event.stopPropagation(), onPointerUp: (event) => event.stopPropagation(), onPointerMove: (event) => event.stopPropagation(), sx: {
                        position: 'absolute',
                        zIndex: zIndex + 1,
                        top: 0,
                        right: 0,
                        left: 0,
                        overflow: 'hidden',
                        p: selectedOutlineOffset + 'px',
                        bgcolor: 'background.paper',
                    } }, props.children))))),
            dragElement && ReactDOM.createPortal(props.dragElement, dragElement),
            dropElement && ReactDOM.createPortal(props.dropElement, dropElement)));
    }
    else {
        return null;
    }
};
const baseZIndex = 400;
const selectedZIndex = 500;
const selectedOutlineOffset = 4;
const selectedToolbarSize = 28;
const selectedLabelBackgroundColor = '#3b97e3';
const selectedLabelColor = '#ffffff';
const selectedOverlayOutlineWidth = 2;
const selectedOverlayOutline = `${selectedOverlayOutlineWidth}px solid ${selectedLabelBackgroundColor}`;
const selectedOverlayBackgroundColor = 'rgba(59, 151, 227, 0.05)';
const hoverOverlayOutline = `1px solid ${selectedLabelBackgroundColor}`;
